import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Target, AlertTriangle, Shield } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { ROLE_RANK } from '@praxara/shared/constants';

export function ComplianceScansPage() {
  const user = useAuthStore((s) => s.user);
  const canScan = user && ROLE_RANK[user.role] >= ROLE_RANK['MANAGER'];
  const qc = useQueryClient();
  const [scanOpen, setScanOpen] = useState(false);

  const scans = useQuery({
    queryKey: ['compliance-scans'],
    queryFn: async () => (await api.get('/api/compliance/scans?pageSize=100')).data,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Compliance scanner</h1>
          <p className="text-sm text-muted-foreground">
            Automated inspection readiness: PSMF (GVP Module II), SOP structure, 21 CFR Part 11, eCTD gap analysis.
          </p>
        </div>
        {canScan && (
          <Button onClick={() => setScanOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" /> New scan
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          icon={Target}
          label="Total scans"
          value={scans.data?.total ?? '—'}
        />
        <StatCard
          icon={Shield}
          label="Avg. readiness"
          value={
            scans.data?.items?.length
              ? `${(
                  scans.data.items
                    .filter((s: Scan) => s.readinessScore != null)
                    .reduce((a: number, s: Scan) => a + (s.readinessScore ?? 0), 0) /
                  Math.max(scans.data.items.filter((s: Scan) => s.readinessScore != null).length, 1)
                ).toFixed(0)}%`
              : '—'
          }
        />
        <StatCard
          icon={AlertTriangle}
          label="Open findings"
          value={
            scans.data?.items?.reduce((a: number, s: Scan) => a + (s.failedChecks ?? 0), 0) ?? '—'
          }
        />
        <StatCard
          icon={Target}
          label="Completed"
          value={
            scans.data?.items?.filter((s: Scan) => s.status === 'completed').length ?? '—'
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Scan history</CardTitle>
          <CardDescription>Readiness score weighs findings by severity (critical=3, major=2, minor=1).</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Findings (P/F/W)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Started</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scans.isLoading && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">Loading...</TableCell></TableRow>
              )}
              {scans.data?.items?.length === 0 && !scans.isLoading && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">No scans yet.</TableCell></TableRow>
              )}
              {scans.data?.items?.map((s: Scan) => (
                <TableRow key={s.id}>
                  <TableCell>
                    <Link to={`/compliance/${s.id}`} className="font-medium hover:underline">{s.scanType.replace(/_/g, ' ')}</Link>
                  </TableCell>
                  <TableCell className="text-xs">{s.targetRef ?? '—'}</TableCell>
                  <TableCell>
                    {s.readinessScore != null ? (
                      <Badge variant={s.readinessScore >= 80 ? 'success' : s.readinessScore >= 50 ? 'warning' : 'destructive'}>
                        {s.readinessScore.toFixed(0)}%
                      </Badge>
                    ) : '—'}
                  </TableCell>
                  <TableCell className="text-xs">
                    {s.passedChecks}/{s.failedChecks}/{s.warningChecks}
                  </TableCell>
                  <TableCell><Badge variant={s.status === 'completed' ? 'success' : s.status === 'failed' ? 'destructive' : 'info'}>{s.status}</Badge></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(s.startedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <NewScanDialog open={scanOpen} onOpenChange={setScanOpen} onCreated={() => qc.invalidateQueries({ queryKey: ['compliance-scans'] })} />
    </div>
  );
}

interface Scan {
  id: string;
  scanType: string;
  targetRef: string | null;
  status: string;
  readinessScore: number | null;
  passedChecks: number;
  failedChecks: number;
  warningChecks: number;
  startedAt: string;
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
        <div>
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function NewScanDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: () => void;
}) {
  const [scanType, setScanType] = useState('PSMF_REVIEW');
  const [documentId, setDocumentId] = useState('');

  const docs = useQuery({
    queryKey: ['documents-all'],
    queryFn: async () => (await api.get('/api/documents?pageSize=100')).data,
    enabled: open,
  });

  const start = useMutation({
    mutationFn: async () => api.post('/api/compliance/scans', { scanType, documentId }),
    onSuccess: () => {
      toast.success('Scan started', 'Running against document...');
      onOpenChange(false);
      onCreated();
    },
    onError: (err) => toast.error('Scan failed', extractApiError(err).message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start compliance scan</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Scan type</Label>
            <select value={scanType} onChange={(e) => setScanType(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="PSMF_REVIEW">PSMF review (GVP Module II)</option>
              <option value="SOP_REVIEW">SOP structure review</option>
              <option value="GVP_COMPLIANCE">GVP compliance</option>
              <option value="CFR_PART_11">21 CFR Part 11</option>
              <option value="EU_ANNEX_11">EU Annex 11</option>
            </select>
          </div>
          <div>
            <Label>Document</Label>
            <select value={documentId} onChange={(e) => setDocumentId(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="">-- select --</option>
              {docs.data?.items?.map((d: { id: string; fileName: string; status: string }) => (
                <option key={d.id} value={d.id} disabled={d.status !== 'TEXT_EXTRACTED' && d.status !== 'PROCESSED'}>
                  {d.fileName} ({d.status})
                </option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => start.mutate()} disabled={!documentId || start.isPending}>
            {start.isPending ? 'Scanning...' : 'Start scan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
